/**
 * LoginForm component for caregiver authentication
 * Handles form validation, API submission, and redirect logic
 */

import { useId, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { env } from '../../lib/env';
import { useAuthStore } from '../../store/auth-store';
import { loginSchema, type LoginFormData } from '../../lib/validation';
import type { User } from '../../types/user';
import { useScreenReaderAnnouncement } from '@/lib/accessibility';
import { useNavigation } from '../../hooks/use-navigation';

export interface LoginFormProps {
  onSuccess?: () => void;
  redirectTo?: string;
}

export function LoginForm({ onSuccess, redirectTo }: LoginFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const login = useAuthStore(state => state.login);
  const { getReturnUrl } = useNavigation();
  const announce = useScreenReaderAnnouncement();

  // Generate unique IDs for form elements
  const emailId = useId();
  const passwordId = useId();
  const emailErrorId = useId();
  const passwordErrorId = useId();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsSubmitting(true);

    try {
      // FastAPI Users login endpoint expects form data, not JSON
      const formData = new URLSearchParams();
      formData.append('username', data.email);
      formData.append('password', data.password);

      const response = await fetch(`${env.apiBaseUrl}/auth/token/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Login failed');
      }

      const loginData = await response.json();

      // Set token in API client
      api.setAuthToken(loginData.access_token);
      api.setRefreshToken(loginData.refresh_token || loginData.access_token);

      const userResponse = await api.get<User>('/api/v1/users/me');

      // Store auth data in Zustand store
      login(loginData.access_token, loginData.refresh_token || loginData.access_token, userResponse);

      // Show different message for new users vs returning users
      const isNewUser = !userResponse.firstName || !userResponse.lastName;
      const welcomeMessage = isNewUser ? 'Welcome!' : 'Welcome back!';
      toast.success(welcomeMessage);
      announce('Successfully logged in. Redirecting...', 'polite');

      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
        return;
      }

      // Determine redirect based on CURRENT state (read fresh from store)
      // Using handleLoginRedirect would use stale closure state
      const currentState = useAuthStore.getState();
      const returnUrl = redirectTo || getReturnUrl();

      if (returnUrl) {
        navigate(returnUrl, { replace: true });
      } else if (currentState.isProfileComplete) {
        navigate('/dashboard', { replace: true });
      } else {
        navigate('/onboarding/personal-info', { replace: true });
      }
    } catch (error) {
      if (error instanceof Error) {
        const errorMessage = error.message || 'Login failed. Please check your credentials.';
        toast.error(errorMessage);
        announce(`Login failed: ${errorMessage}`, 'assertive');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" noValidate>
      {/* Email Field */}
      <div>
        <label htmlFor={emailId} className="block font-display text-sm font-semibold text-night-900 mb-2">
          Email Address
        </label>
        <Input
          id={emailId}
          type="email"
          autoComplete="email"
          placeholder="you@example.com"
          {...register('email')}
          aria-invalid={!!errors.email}
          aria-describedby={errors.email ? emailErrorId : undefined}
          aria-required={true}
          disabled={isSubmitting}
        />
        {errors.email && (
          <p id={emailErrorId} className="mt-1 text-sm text-error" role="alert">
            {errors.email.message}
          </p>
        )}
      </div>

      {/* Password Field */}
      <div>
        <label
          htmlFor={passwordId}
          className="block font-display text-sm font-semibold text-night-900 mb-2"
        >
          Password
        </label>
        <div className="relative">
          <Input
            id={passwordId}
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            placeholder="Enter your password"
            {...register('password')}
            className="pr-10"
            aria-invalid={!!errors.password}
            aria-describedby={errors.password ? passwordErrorId : undefined}
            aria-required={true}
            disabled={isSubmitting}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-night-300 hover:text-night-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blueviolet-light focus-visible:ring-offset-2 rounded"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            tabIndex={0}
          >
            {showPassword ? (
              <EyeOff className="h-5 w-5" aria-hidden="true" />
            ) : (
              <Eye className="h-5 w-5" aria-hidden="true" />
            )}
          </button>
        </div>
        {errors.password && (
          <p id={passwordErrorId} className="mt-1 text-sm text-error" role="alert">
            {errors.password.message}
          </p>
        )}
      </div>

      {/* Remember Me Checkbox - disabled until backend supports different token lifetimes
      <div className="flex items-center space-x-2">
        <Checkbox
          id={rememberMeId}
          checked={rememberMe}
          onCheckedChange={checked => setValue('rememberMe', checked === true)}
          disabled={isSubmitting}
          aria-describedby={`${rememberMeId}-description`}
        />
        <label htmlFor={rememberMeId} className="font-body text-sm text-gray-700 cursor-pointer">
          Remember me
        </label>
        <span id={`${rememberMeId}-description`} className="sr-only">
          Keep me logged in on this device
        </span>
      </div>
      */}

      {/* Submit Button */}
      <Button
        type="submit"
        className="w-full"
        disabled={isSubmitting}
        size="lg"
        aria-describedby="login-button-description"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" aria-hidden="true" />
            Logging in...
          </>
        ) : (
          'Log In'
        )}
      </Button>
      <span id="login-button-description" className="sr-only">
        Submit the login form to access your CareCal account
      </span>
    </form>
  );
}
